import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { useCallback, useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useCartContext } from '@magento/peregrine/lib/context/cart';
import { useUserContext } from '@magento/peregrine/lib/context/user';
import { useToasts } from '@magento/peregrine';
import { useDispatch } from 'react-redux';
import { appendOptionsToPayload } from '@magento/peregrine/lib/util/appendOptionsToPayload';
import { findMatchingVariant } from '@magento/peregrine/lib/util/findMatchingProductVariant';
import { isProductConfigurable } from '@magento/peregrine/lib/util/isProductConfigurable';
import { isSupportedProductType as isSupported } from '@magento/peregrine/lib/util/isSupportedProductType';
import { deriveErrorMessage } from '@magento/peregrine/lib/util/deriveErrorMessage'; //'../../util/deriveErrorMessage';

import mergeOperations from '@magento/peregrine/lib/util/shallowMerge';
import defaultOperations from './productFullDetail.gql';
import { deriveOptionSelectionsFromProduct } from '../../../../hooks/utils';
import { deriveOptionSelectionsFromVariant } from '../../../../hooks/utils';
import { setQuotes } from '../../store/actions/quotes/asyncActions';
import { GET_RELATED_PRODUCTS_QUERY } from '../RootComponents/Product/product.gql';
import { GET_VARIANT_DETAILS_QUERY } from '../RootComponents/Product/product.gql';
import { GET_CUSTOMER_WISHLIST } from '@magento/peregrine/lib/talons/WishlistPage/wishlistPage.gql';
import { useAvailableOutlet } from './AvailableOutlet/useAvailableOutlet';
import { EXISTS_IN_FAVORITES } from '@magento/peregrine/lib/talons/Gallery/gallery.gql.ee';
var INITIAL_OPTION_CODES = new Map();
var INITIAL_OPTION_SELECTIONS = new Map();
var OUT_OF_STOCK_CODE = 'OUT_OF_STOCK';

var deriveOptionCodesFromProduct = function deriveOptionCodesFromProduct(product) {
  // If this is a simple product it has no option codes.
  if (!isProductConfigurable(product)) {
    return INITIAL_OPTION_CODES;
  } // Initialize optionCodes based on the options of the product.


  var initialOptionCodes = new Map();

  var _iterator = _createForOfIteratorHelper(product.configurable_options),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _step.value,
          attribute_id = _step$value.attribute_id,
          attribute_code = _step$value.attribute_code;
      initialOptionCodes.set(attribute_id, attribute_code);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return initialOptionCodes;
}; // Similar to deriving the initial codes for each option.


var getIsMissingOptions = function getIsMissingOptions(product, optionSelections) {
  // Non-configurable products can't be missing options.
  if (!isProductConfigurable(product)) {
    return false;
  } // Configurable products are missing options if we have fewer
  // option selections than the product has options.


  var configurable_options = product.configurable_options;
  var numProductOptions = configurable_options.length;
  var numProductSelections = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length;
  return numProductSelections < numProductOptions;
};

var getIsOutOfStock = function getIsOutOfStock(product, optionCodes, optionSelections) {
  var stock_status = product.stock_status,
      variants = product.variants;
  var isConfigurable = isProductConfigurable(product);
  var optionsSelected = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length > 0;

  if (isConfigurable && optionsSelected) {
    var _item$product;

    var item = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: variants
    });
    return (item === null || item === void 0 ? void 0 : (_item$product = item.product) === null || _item$product === void 0 ? void 0 : _item$product.stock_status) === OUT_OF_STOCK_CODE;
  }

  return stock_status === OUT_OF_STOCK_CODE;
};

var isDefaultSmartStringSelected = function isDefaultSmartStringSelected(product, optionCodes, optionSelections, defaultOptionSelections) {
  var variants = product.variants;
  var isConfigurable = isProductConfigurable(product); // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.

  var optionsSelected = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length > 0;
  var isDefaultSelected = true;

  if (isConfigurable && optionsSelected) {
    // If any of the possible variants matches the selection add that
    // variant's image to the media gallery. NOTE: This _can_, and does,
    // include variants such as size. If Magento is configured to display
    // an image for a size attribute, it will render that image.
    var item = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: variants
    });
    optionSelections === null || optionSelections === void 0 ? void 0 : optionSelections.forEach(function (value, index) {
      if (defaultOptionSelections.get(index) != value) {
        isDefaultSelected = false;
      }
    });
  }

  return isDefaultSelected;
};

var getMediaGalleryEntries = function getMediaGalleryEntries(product, optionCodes, optionSelections, defaultOptionSelections, outletItems) {
  var value = [];
  var selectedNonDefaultVariantImages = []; // console.log(product);
  // const { media_gallery_entries, media_gallery, variants } = product;

  var media_gallery = product.media_gallery,
      variants = product.variants;
  var isConfigurable = isProductConfigurable(product);
  var mgValue = media_gallery; // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.

  var optionsSelected = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length > 0;

  if (isConfigurable && optionsSelected) {
    var _item$product2, _item$product3;

    // If any of the possible variants matches the selection add that
    // variant's image to the media gallery. NOTE: This _can_, and does,
    // include variants such as size. If Magento is configured to display
    // an image for a size attribute, it will render that image.
    var item = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: variants
    });
    var selectedIsDefault = true;
    optionSelections === null || optionSelections === void 0 ? void 0 : optionSelections.forEach(function (value, index) {
      if (defaultOptionSelections.get(index) != value) {
        selectedIsDefault = false;
      }
    });
    selectedNonDefaultVariantImages = !selectedIsDefault && item !== null && item !== void 0 && (_item$product2 = item.product) !== null && _item$product2 !== void 0 && _item$product2.media_gallery ? item === null || item === void 0 ? void 0 : (_item$product3 = item.product) === null || _item$product3 === void 0 ? void 0 : _item$product3.media_gallery.filter(function (image) {
      var _image$url;

      return ((_image$url = image.url) === null || _image$url === void 0 ? void 0 : _image$url.split('/').reverse()[0].replace(/^.*?[_]/, '').replace(/\.[^/.]+$/, '').replace(/_[0-9]+?$/, '')) > 59;
    }) : [];
  }

  if (outletItems) {
    var _mgValue;

    // Exclude all the images from the gallery that end up _5x if the smart string of the
    // outlet items is not a part of their name
    mgValue = (_mgValue = mgValue) === null || _mgValue === void 0 ? void 0 : _mgValue.filter(function (image) {
      var regex = /\/([^\/_]*_(\d+))/;
      var result = image.url.match(regex);
      var imageSortNumber = parseInt(result[2]);
      return imageSortNumber > 59 || imageSortNumber < 50 || outletItems.filter(function (outletItem) {
        return image.url.toLowerCase().includes(outletItem.hf_smart_string.toLowerCase());
      }).length > 0;
    }); // console.log(outletItems);
  } // move the main image to the front
  // let mainImage = value?.find(image => image.types?.length && image.types?.includes('image'));
  // let mgValueMain = mainImage ? mgValue.find(image => image?.url?.includes(mainImage?.file)) : false;
  //
  // *** This code is not relevant any longer because the ordering is being done in productSlider (at least for image gallery on PDP)
  // let mgValueMain = mgValue?.find(image =>
  //     image.url?.split('/').reverse()[0]
  //         .replace(/^.*?[_]/, '')
  //         .replace(/\.[^/.]+$/, '')
  //         .replace(/_[0-9]+?$/, '') == '1'
  // );
  // if(mgValueMain) {
  //     mgValue = mgValue?.filter(image => image.url !== mgValueMain.url);
  //     mgValue.unshift(mgValueMain);
  // }
  // ***


  return {
    // mediaGalleryEntries: value,
    mediaGallery: [].concat(_toConsumableArray(selectedNonDefaultVariantImages || []), _toConsumableArray(mgValue || []))
  };
}; // We only want to display breadcrumbs for one category on a PDP even if a
// product has multiple related categories. This function filters and selects
// one category id for that purpose.


var getBreadcrumbCategoryId = function getBreadcrumbCategoryId(categories) {
  // Exit if there are no categories for this product.
  if (!categories || !categories.length) {
    return;
  }

  var breadcrumbSet = new Set();
  categories.forEach(function (_ref) {
    var breadcrumbs = _ref.breadcrumbs;
    // breadcrumbs can be `null`...
    (breadcrumbs || []).forEach(function (_ref2) {
      var category_id = _ref2.category_id;
      return breadcrumbSet.add(category_id);
    });
  }); // Until we can get the single canonical breadcrumb path to a product we
  // will just return the first category id of the potential leaf categories.

  var leafCategory = categories.find(function (category) {
    return !breadcrumbSet.has(category.uid);
  }); // If we couldn't find a leaf category then just use the first category
  // in the list for this product.

  return leafCategory.uid || categories[0].uid;
};

var getConfigPrice = function getConfigPrice(product, optionCodes, optionSelections) {
  var value;
  var variants = product.variants;
  var isConfigurable = isProductConfigurable(product);
  var optionsSelected = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length > 0;

  if (!isConfigurable || !optionsSelected) {
    var _product$price_range, _product$price_range$, _product$price_range2, _product$price_range3;

    value = (_product$price_range = product.price_range) !== null && _product$price_range !== void 0 && (_product$price_range$ = _product$price_range.maximum_price) !== null && _product$price_range$ !== void 0 && _product$price_range$.final_price ? (_product$price_range2 = product.price_range) === null || _product$price_range2 === void 0 ? void 0 : (_product$price_range3 = _product$price_range2.maximum_price) === null || _product$price_range3 === void 0 ? void 0 : _product$price_range3.final_price : product.price.regularPrice.amount;
  } else {
    var _item$product4, _item$product4$price_, _item$product4$price_2, _item$product5, _item$product5$price_, _item$product5$price_2, _item$product6, _item$product6$price_, _item$product6$price_2, _item$product7, _item$product7$price_, _item$product7$price_2, _product$price_range4, _product$price_range5, _product$price_range6, _product$price_range7, _product$price_range8, _product$price_range9, _product$price_range10, _product$price_range11;

    var item = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: variants
    });
    value = item ? (_item$product4 = item.product) !== null && _item$product4 !== void 0 && (_item$product4$price_ = _item$product4.price_range) !== null && _item$product4$price_ !== void 0 && (_item$product4$price_2 = _item$product4$price_.minimum_price) !== null && _item$product4$price_2 !== void 0 && _item$product4$price_2.final_price ? (_item$product5 = item.product) === null || _item$product5 === void 0 ? void 0 : (_item$product5$price_ = _item$product5.price_range) === null || _item$product5$price_ === void 0 ? void 0 : (_item$product5$price_2 = _item$product5$price_.minimum_price) === null || _item$product5$price_2 === void 0 ? void 0 : _item$product5$price_2.final_price : (_item$product6 = item.product) !== null && _item$product6 !== void 0 && (_item$product6$price_ = _item$product6.price_range) !== null && _item$product6$price_ !== void 0 && (_item$product6$price_2 = _item$product6$price_.maximum_price) !== null && _item$product6$price_2 !== void 0 && _item$product6$price_2.final_price ? (_item$product7 = item.product) === null || _item$product7 === void 0 ? void 0 : (_item$product7$price_ = _item$product7.price_range) === null || _item$product7$price_ === void 0 ? void 0 : (_item$product7$price_2 = _item$product7$price_.maximum_price) === null || _item$product7$price_2 === void 0 ? void 0 : _item$product7$price_2.final_price : item.product.price.regularPrice.amount : (_product$price_range4 = product.price_range) !== null && _product$price_range4 !== void 0 && (_product$price_range5 = _product$price_range4.minimum_price) !== null && _product$price_range5 !== void 0 && _product$price_range5.final_price ? (_product$price_range6 = product.price_range) === null || _product$price_range6 === void 0 ? void 0 : (_product$price_range7 = _product$price_range6.minimum_price) === null || _product$price_range7 === void 0 ? void 0 : _product$price_range7.final_price : (_product$price_range8 = product.price_range) !== null && _product$price_range8 !== void 0 && (_product$price_range9 = _product$price_range8.maximum_price) !== null && _product$price_range9 !== void 0 && _product$price_range9.final_price ? (_product$price_range10 = product.price_range) === null || _product$price_range10 === void 0 ? void 0 : (_product$price_range11 = _product$price_range10.maximum_price) === null || _product$price_range11 === void 0 ? void 0 : _product$price_range11.final_price : product.price.regularPrice.amount;
  }

  return value;
};

var getDisabledValuesMap = function getDisabledValuesMap(product, optionSelections) {
  var isConfigurable = isProductConfigurable(product);

  if (!isConfigurable) {
    return new Map();
  }
  /**
  We need to add logic that will take into account impossible combinations of the configurable
  attributes. Impossible means that a certain target combination is not available (product out
  of stock or something else).
   October 6th, 2022, UPDATE: The client specifically expressed there's no interdependency
      between the options and that the options that are not available should just be hidden.
      So that has been implemented with this commit.
   1. User lands on config PDP. Defautl combination is selected.
  2. For each of the selected attribute values:
      2.1 Go through all other available config attribute values.
          2.1.1 Check if there's a combination (product variant) which exists that
              contains both values from currently investigated selected attr val (2.)
              and the currently investigated other avail config attr val (2.1)
          2.1.2 If YES, make the currently investigated other aval config attr val (2.1)
              selectable.
          2.1.3 If NO, make the currently investigated other aval config attr val (2.1)
              unselectable/disabled with appropriate wording.
    */


  var attributeMap = new Map();

  var _iterator2 = _createForOfIteratorHelper(product.configurable_options),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _step2$value = _step2.value,
          attribute_id = _step2$value.attribute_id,
          attribute_code = _step2$value.attribute_code;
      attributeMap.set(attribute_code, attribute_id);
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  var map = new Map();

  var _iterator3 = _createForOfIteratorHelper(product.configurable_options),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var _step3$value = _step3.value,
          _attribute_id = _step3$value.attribute_id,
          _attribute_code = _step3$value.attribute_code,
          values = _step3$value.values;
      var innerMap = new Map();

      var _iterator4 = _createForOfIteratorHelper(values),
          _step4;

      try {
        for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
          var value_index = _step4.value.value_index;
          var status = false; // if this value_index is actually one of selections
          // the value is always false

          if (value_index !== (optionSelections === null || optionSelections === void 0 ? void 0 : optionSelections.get(_attribute_id))) {
            status = true; // so at this point we're checking all values of attribute_id
            // which are not selected if they exist in combination with
            // other SELECTED values from other attributes from the configuration
            // pool

            status = shouldValueBeDisabled(_attribute_id, attributeMap, value_index, product.variants, optionSelections);
          }

          innerMap.set(value_index, status);
        }
      } catch (err) {
        _iterator4.e(err);
      } finally {
        _iterator4.f();
      }

      map.set(_attribute_id, innerMap);
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return map;
};

var shouldValueBeDisabled = function shouldValueBeDisabled(currentAttributeId, attributeMap, value_index, variants, optionSelections) {
  var targetSelection = new Map();
  targetSelection.set(currentAttributeId, value_index);
  optionSelections.forEach(function (value_selected, attribute_id) {
    if (currentAttributeId != attribute_id) {
      targetSelection.set(attribute_id, value_selected);
    }
  });

  var _iterator5 = _createForOfIteratorHelper(variants),
      _step5;

  try {
    for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
      var variant = _step5.value;
      var targetExists = true;

      var _iterator6 = _createForOfIteratorHelper(variant.attributes),
          _step6;

      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var attribute = _step6.value;
          var target_attribute_id = attributeMap.get(attribute.code);
          var target_value_index = targetSelection.get(target_attribute_id);

          if (target_value_index != attribute.value_index) {
            targetExists = false;
          }
        }
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }

      if (targetExists) {
        return false;
      }
    }
  } catch (err) {
    _iterator5.e(err);
  } finally {
    _iterator5.f();
  }

  return true;
};

var getSkuData = function getSkuData(product, optionCodes, optionSelections) {
  var productData;
  var variants = product.variants;
  var isConfigurable = isProductConfigurable(product);
  var optionsSelected = Array.from(optionSelections.values()).filter(function (value) {
    return !!value;
  }).length > 0;

  if (!isConfigurable || !optionsSelected) {
    productData = {
      itemId: product.sku,
      smartString: 'Base Item',
      // customAttributes: product.custom_attributes,
      weight: null,
      configCustomAttributes: product.custom_attributes
    };
  } else {
    var item = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: variants
    });
    productData = {
      itemId: item.product.sku,
      smartString: item.product.hf_smart_string,
      // customAttributes: item.product.custom_attributes,
      weight: item.product.weight,
      configCustomAttributes: product.custom_attributes
    };
  }

  return productData;
};
/**
 * @param {GraphQLDocument} props.addConfigurableProductToCartMutation - configurable product mutation
 * @param {GraphQLDocument} props.addSimpleProductToCartMutation - configurable product mutation
 * @param {Object.<string, GraphQLDocument>} props.operations - collection of operation overrides merged into defaults
 * @param {Object} props.product - the product, see RootComponents/Product
 *
 * @returns {{
 *  breadcrumbCategoryId: string|undefined,
 *  errorMessage: string|undefined,
 *  handleAddToCart: func,
 *  handleSelectionChange: func,
 *  handleSetQuantity: func,
 *  isAddToCartDisabled: boolean,
 *  isSupportedProductType: boolean,
 *  mediaGalleryEntries: array,
 *  productDetails: object,
 *  quantity: number
 * }}
 */


export var useProductFullDetail = function useProductFullDetail(props) {
  var _collectionsData$coll, _dataRelated$products, _dataRelated$products2;

  var _useToasts = useToasts(),
      _useToasts2 = _slicedToArray(_useToasts, 2),
      addToast = _useToasts2[1].addToast;

  var dispatch = useDispatch();
  var addConfigurableProductToCartMutation = props.addConfigurableProductToCartMutation,
      addSimpleProductToCartMutation = props.addSimpleProductToCartMutation,
      product = props.product;
  var hasDeprecatedOperationProp = !!(addConfigurableProductToCartMutation || addSimpleProductToCartMutation);
  var operations = mergeOperations(defaultOperations, props.operations);
  var productType = product.__typename;
  var isSupportedProductType = isSupported(productType);

  var _useCartContext = useCartContext(),
      _useCartContext2 = _slicedToArray(_useCartContext, 1),
      cartId = _useCartContext2[0].cartId;

  var _useUserContext = useUserContext(),
      _useUserContext2 = _slicedToArray(_useUserContext, 1),
      isSignedIn = _useUserContext2[0].isSignedIn;

  var _useIntl = useIntl(),
      formatMessage = _useIntl.formatMessage;

  var tagValues = useMemo(function () {
    var _ref3;

    return (_ref3 = ['hf_best_seller', 'hf_designer_preference', 'hf_new_designs']) === null || _ref3 === void 0 ? void 0 : _ref3.reduce(function (acc, key) {
      var _product$variants;

      var found = product === null || product === void 0 ? void 0 : (_product$variants = product.variants) === null || _product$variants === void 0 ? void 0 : _product$variants.some(function (item) {
        var _item$product8;

        return (item === null || item === void 0 ? void 0 : (_item$product8 = item.product) === null || _item$product8 === void 0 ? void 0 : _item$product8[key]) === 1;
      });
      acc[key] = found ? 1 : null;
      return acc;
    }, {});
  }, [product.variants]);

  var _useQuery = useQuery(operations.getWishlistConfigQuery, {
    fetchPolicy: 'cache-and-network'
  }),
      storeConfigData = _useQuery.data;

  var _useQuery2 = useQuery(operations.getCollections, {
    fetchPolicy: 'cache-and-network'
  }),
      collectionsData = _useQuery2.data;

  var _useMutation = useMutation(addConfigurableProductToCartMutation || operations.addConfigurableProductToCartMutation),
      _useMutation2 = _slicedToArray(_useMutation, 2),
      addConfigurableProductToCart = _useMutation2[0],
      _useMutation2$ = _useMutation2[1],
      errorAddingConfigurableProduct = _useMutation2$.error,
      isAddConfigurableLoading = _useMutation2$.loading;

  var _useMutation3 = useMutation(addSimpleProductToCartMutation || operations.addSimpleProductToCartMutation),
      _useMutation4 = _slicedToArray(_useMutation3, 2),
      addSimpleProductToCart = _useMutation4[0],
      _useMutation4$ = _useMutation4[1],
      errorAddingSimpleProduct = _useMutation4$.error,
      isAddSimpleLoading = _useMutation4$.loading;

  var _useMutation5 = useMutation(operations.addProductToCartMutation),
      _useMutation6 = _slicedToArray(_useMutation5, 2),
      addProductToCart = _useMutation6[0],
      _useMutation6$ = _useMutation6[1],
      addToCartOutcome = _useMutation6$.data,
      errorAddingProductToCart = _useMutation6$.error,
      isAddProductLoading = _useMutation6$.loading;

  var breadcrumbCategoryId = useMemo(function () {
    return getBreadcrumbCategoryId(product.categories);
  }, [product.categories]);
  var derivedOptionSelections = useMemo(function () {
    return deriveOptionSelectionsFromProduct(product, INITIAL_OPTION_SELECTIONS);
  }, [product]);

  var _useState = useState(product === null || product === void 0 ? void 0 : product.custom_attributes),
      _useState2 = _slicedToArray(_useState, 2),
      customAttributes = _useState2[0],
      setCustomAttributes = _useState2[1];

  var _useState3 = useState(derivedOptionSelections),
      _useState4 = _slicedToArray(_useState3, 2),
      optionSelections = _useState4[0],
      setOptionSelections = _useState4[1];

  var _useQuery3 = useQuery(GET_RELATED_PRODUCTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    // fetchPolicy: 'no-cache',
    variables: {
      urlKey: product.url_key
    }
  }),
      loadingRelated = _useQuery3.loading,
      errorRelated = _useQuery3.error,
      dataRelated = _useQuery3.data;
  /**
   * This part is added to control the state of option values and not allow the visitor
   * to select values which are not available (picking a combination of config attribute
   * which targets a child simple product with that combination which either doesn't exist
   * or is disabled)
   */


  var disabledValuesMap = useMemo(function () {
    return getDisabledValuesMap(product, optionSelections);
  }, [product, optionSelections]); // const [disabledValuesMap, setDisabledValuesMap] = useState(
  //     derivedDisabledValuesMap
  // )

  /** end */

  var derivedOptionCodes = useMemo(function () {
    return deriveOptionCodesFromProduct(product);
  }, [product]);

  var _useState5 = useState(derivedOptionCodes),
      _useState6 = _slicedToArray(_useState5, 1),
      optionCodes = _useState6[0];

  var isMissingOptions = useMemo(function () {
    return getIsMissingOptions(product, optionSelections);
  }, [product, optionSelections]);
  var isOutOfStock = useMemo(function () {
    return getIsOutOfStock(product, optionCodes, optionSelections);
  }, [product, optionCodes, optionSelections]);

  var _ref4 = product.__typename == 'GroupedProduct' ? useAvailableOutlet({
    outletSku: product.sku
  }) : {
    outletItems: null
  },
      outletItems = _ref4.outletItems;

  var _useMemo = useMemo(function () {
    return getMediaGalleryEntries(product, optionCodes, optionSelections, derivedOptionSelections, outletItems);
  }, [product, optionCodes, optionSelections, outletItems]),
      mediaGallery = _useMemo.mediaGallery;

  var defaultSmartStringSelected = useMemo(function () {
    return isDefaultSmartStringSelected(product, optionCodes, optionSelections, derivedOptionSelections);
  }, [product, optionCodes, optionSelections]); // The map of ids to values (and their uids)
  // For example:
  // { "179" => [{ uid: "abc", value_index: 1 }, { uid: "def", value_index: 2 }]}

  var attributeIdToValuesMap = useMemo(function () {
    var map = new Map(); // For simple items, this will be an empty map.

    var options = product.configurable_options || [];

    var _iterator7 = _createForOfIteratorHelper(options),
        _step7;

    try {
      for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
        var _step7$value = _step7.value,
            attribute_id = _step7$value.attribute_id,
            values = _step7$value.values;
        map.set(attribute_id, values);
      }
    } catch (err) {
      _iterator7.e(err);
    } finally {
      _iterator7.f();
    }

    return map;
  }, [product.configurable_options]); // An array of selected option uids. Useful for passing to mutations.
  // For example:
  // ["abc", "def"]

  var selectedOptionsArray = useMemo(function () {
    var selectedOptions = [];
    optionSelections.forEach(function (value, key) {
      var values = attributeIdToValuesMap.get(key);
      var selectedValue = values === null || values === void 0 ? void 0 : values.find(function (item) {
        return item.value_index === value;
      });

      if (selectedValue) {
        selectedOptions.push(selectedValue.uid);
      }
    });
    return selectedOptions;
  }, [attributeIdToValuesMap, optionSelections]);
  var handleAddToCart = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(formValues) {
      var quantity, payload, variables, _variables;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              quantity = formValues.quantity;
              /*
                  @deprecated in favor of general addProductsToCart mutation. Will support until the next MAJOR.
               */

              if (!hasDeprecatedOperationProp) {
                _context.next = 31;
                break;
              }

              payload = {
                item: product,
                productType: productType,
                quantity: quantity
              };

              if (isProductConfigurable(product)) {
                appendOptionsToPayload(payload, optionSelections, optionCodes);
              }

              if (!isSupportedProductType) {
                _context.next = 28;
                break;
              }

              variables = {
                cartId: cartId,
                parentSku: payload.parentSku,
                product: payload.item,
                quantity: payload.quantity,
                sku: payload.item.sku
              }; // Use the proper mutation for the type.

              if (!(productType === 'SimpleProduct')) {
                _context.next = 17;
                break;
              }

              _context.prev = 7;
              _context.next = 10;
              return addSimpleProductToCart({
                variables: variables
              });

            case 10:
              _context.next = 15;
              break;

            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](7);
              return _context.abrupt("return");

            case 15:
              _context.next = 26;
              break;

            case 17:
              if (!(productType === 'ConfigurableProduct')) {
                _context.next = 26;
                break;
              }

              _context.prev = 18;
              _context.next = 21;
              return addConfigurableProductToCart({
                variables: variables
              });

            case 21:
              _context.next = 26;
              break;

            case 23:
              _context.prev = 23;
              _context.t1 = _context["catch"](18);
              return _context.abrupt("return");

            case 26:
              _context.next = 29;
              break;

            case 28:
              console.error('Unsupported product type. Cannot add to cart.');

            case 29:
              _context.next = 41;
              break;

            case 31:
              _variables = {
                cartId: cartId,
                product: {
                  sku: product.sku,
                  quantity: quantity
                },
                entered_options: [{
                  uid: product.uid,
                  value: product.name
                }]
              };
              if (selectedOptionsArray.length) _variables.product.selected_options = selectedOptionsArray;
              _context.prev = 33;
              _context.next = 36;
              return addProductToCart({
                variables: _variables
              });

            case 36:
              _context.next = 41;
              break;

            case 38:
              _context.prev = 38;
              _context.t2 = _context["catch"](33);
              return _context.abrupt("return");

            case 41:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[7, 12], [18, 23], [33, 38]]);
    }));

    return function (_x) {
      return _ref5.apply(this, arguments);
    };
  }(), [addConfigurableProductToCart, addProductToCart, addSimpleProductToCart, cartId, hasDeprecatedOperationProp, isSupportedProductType, optionCodes, optionSelections, product, productType, selectedOptionsArray]);

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isReset = _useState8[0],
      setIsReset = _useState8[1];

  var handleResetProductConfiguration = function handleResetProductConfiguration() {
    setOptionSelections(derivedOptionSelections); // console.log("reset the configurion!");

    setIsReset(true);
  };

  var setProductConfiguration = useCallback(function (smartString) {
    var _product$variants$fin;

    var targetVariant = (_product$variants$fin = product.variants.find(function (variant) {
      return variant.product.hf_smart_string == smartString;
    })) !== null && _product$variants$fin !== void 0 ? _product$variants$fin : false;
    var newOptionSelections = deriveOptionSelectionsFromVariant(product, targetVariant);

    if (!!newOptionSelections) {
      setOptionSelections(deriveOptionSelectionsFromVariant(product, targetVariant));
    }
  }, [product]);
  var handleSelectionChange = useCallback(function (optionId, selection) {
    var _product$configurable, _product$configurable2;

    setIsReset(false); // We must create a new Map here so that React knows that the value
    // of optionSelections has changed.

    var nextOptionSelections = new Map(_toConsumableArray(optionSelections));
    nextOptionSelections.set(optionId, selection); // Now verify that this option selections is possible at all

    var changedAttributeCode = product === null || product === void 0 ? void 0 : (_product$configurable = product.configurable_options) === null || _product$configurable === void 0 ? void 0 : (_product$configurable2 = _product$configurable.find(function (item) {
      return item.attribute_id == optionId;
    })) === null || _product$configurable2 === void 0 ? void 0 : _product$configurable2.attribute_code; // now try to find the matching variant before assigning value

    var matchingVariant = findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: nextOptionSelections,
      variants: product.variants
    });

    if (!matchingVariant) {
      // now we're setting the optionselection to this randomvariant values
      var hfFinishCurrentValue = null;

      var _iterator8 = _createForOfIteratorHelper(optionSelections),
          _step8;

      try {
        for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
          var _step8$value = _slicedToArray(_step8.value, 2),
              id = _step8$value[0],
              value = _step8$value[1];

          var code = optionCodes.get(id);

          if (code == 'hf_finish') {
            hfFinishCurrentValue = value;
          }
        }
      } catch (err) {
        _iterator8.e(err);
      } finally {
        _iterator8.f();
      }

      var possibleVariants = product.variants.filter(function (variant) {
        return variant.attributes.find(function (attr) {
          return attr.code == changedAttributeCode && attr.value_index == selection;
        });
      }); // we're making a hardcoded exception for the Finish
      // meaning we'll narrow down the selection of random variants according to currently selected finish

      var randomVariant = possibleVariants.find(function (variant) {
        return variant.attributes.find(function (attr) {
          return attr.code == 'hf_finish' && attr.value_index == hfFinishCurrentValue;
        });
      }) || possibleVariants[0]; // now we're setting the optionselection to this randomvariant values

      var _iterator9 = _createForOfIteratorHelper(nextOptionSelections),
          _step9;

      try {
        var _loop = function _loop() {
          var _randomVariant$attrib;

          var _step9$value = _slicedToArray(_step9.value, 2),
              id = _step9$value[0],
              value = _step9$value[1];

          if (optionId == id) return "continue";
          var code = optionCodes.get(id);
          nextOptionSelections.set(id, (_randomVariant$attrib = randomVariant.attributes.find(function (attr) {
            return attr.code == code;
          })) === null || _randomVariant$attrib === void 0 ? void 0 : _randomVariant$attrib.value_index);
        };

        for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
          var _ret = _loop();

          if (_ret === "continue") continue;
        }
      } catch (err) {
        _iterator9.e(err);
      } finally {
        _iterator9.f();
      }
    }

    setOptionSelections(nextOptionSelections);
  }, [optionSelections]);
  var productPrice = useMemo(function () {
    return getConfigPrice(product, optionCodes, optionSelections);
  }, [product, optionCodes, optionSelections]); // Normalization object for product details we need for rendering.

  var productDetails = {
    description: product.description,
    short_description: product.short_description,
    name: product.name,
    price: productPrice,
    sku: product.sku,
    media_gallery: product === null || product === void 0 ? void 0 : product.media_gallery
  };
  var derivedErrorMessage = useMemo(function () {
    var _addToCartOutcome$add, _addToCartOutcome$add2;

    return deriveErrorMessage([errorAddingSimpleProduct, errorAddingConfigurableProduct, errorAddingProductToCart].concat(_toConsumableArray((_addToCartOutcome$add = addToCartOutcome === null || addToCartOutcome === void 0 ? void 0 : (_addToCartOutcome$add2 = addToCartOutcome.addProductsToCart) === null || _addToCartOutcome$add2 === void 0 ? void 0 : _addToCartOutcome$add2.user_errors) !== null && _addToCartOutcome$add !== void 0 ? _addToCartOutcome$add : [])));
  }, [errorAddingConfigurableProduct, errorAddingProductToCart, errorAddingSimpleProduct, addToCartOutcome]);
  var successAddToCartToast = useMemo(function () {
    var _addToCartOutcome$add3, _addToCartOutcome$add4;

    var successToast = false;
    var miniCartBtn = document.getElementById('mini-cart-btn');

    if (addToCartOutcome && !((addToCartOutcome === null || addToCartOutcome === void 0 ? void 0 : (_addToCartOutcome$add3 = addToCartOutcome.addProductsToCart) === null || _addToCartOutcome$add3 === void 0 ? void 0 : (_addToCartOutcome$add4 = _addToCartOutcome$add3.user_errors) === null || _addToCartOutcome$add4 === void 0 ? void 0 : _addToCartOutcome$add4.length) > 0)) {
      successToast = {
        message: 'Successfully added product to cart!',
        type: 'success',
        timeout: '5000'
      };

      try {
        miniCartBtn === null || miniCartBtn === void 0 ? void 0 : miniCartBtn.click();
        setTimeout(function () {
          return miniCartBtn === null || miniCartBtn === void 0 ? void 0 : miniCartBtn.click();
        }, 4000);
      } catch (error) {}
    }

    return successToast;
  }, [addToCartOutcome]);
  var wishlistItemOptions = useMemo(function () {
    var options = {
      quantity: 1,
      sku: product.sku
    };

    if (productType === 'ConfigurableProduct') {
      options.selected_options = selectedOptionsArray;
    }

    return options;
  }, [product, productType, selectedOptionsArray]);
  var wishlistButtonProps = {
    buttonText: function buttonText(isSelected) {
      return isSelected ? formatMessage({
        id: 'wishlistButton.addedText',
        defaultMessage: 'Added to Favorites'
      }) : formatMessage({
        id: 'wishlistButton.addText',
        defaultMessage: 'Add to Favorites'
      });
    },
    item: wishlistItemOptions,
    storeConfig: storeConfigData ? storeConfigData.storeConfig : {}
  };

  var _useMemo2 = useMemo(function () {
    return getSkuData(product, optionCodes, optionSelections);
  }, [optionSelections]),
      configuredItemId = _useMemo2.itemId,
      configuredSmartString = _useMemo2.smartString,
      weight = _useMemo2.weight,
      configCustomAttributes = _useMemo2.configCustomAttributes;

  var _useLazyQuery = useLazyQuery(GET_VARIANT_DETAILS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      sku: product.sku,
      variantSku: configuredItemId
    }
  }),
      _useLazyQuery2 = _slicedToArray(_useLazyQuery, 2),
      getVariantDetails = _useLazyQuery2[0],
      _useLazyQuery2$ = _useLazyQuery2[1],
      variantData = _useLazyQuery2$.data,
      variantError = _useLazyQuery2$.error,
      variantLoading = _useLazyQuery2$.loading;

  useEffect(function () {
    if (isProductConfigurable(product) && Array.from(optionSelections.values()).filter(function (value) {
      return !!value;
    }).length > 0 && configuredItemId) {
      getVariantDetails();
    }
  }, [product, optionSelections, configuredItemId]);
  useEffect(function () {
    var _variantData$products, _variantData$products2, _variantData$products3, _variantData$products4;

    if (variantData !== null && variantData !== void 0 && (_variantData$products = variantData.products) !== null && _variantData$products !== void 0 && (_variantData$products2 = _variantData$products.items[0]) !== null && _variantData$products2 !== void 0 && (_variantData$products3 = _variantData$products2.variants[0]) !== null && _variantData$products3 !== void 0 && (_variantData$products4 = _variantData$products3.product) !== null && _variantData$products4 !== void 0 && _variantData$products4.custom_attributes) {
      var _variantData$products5, _variantData$products6, _variantData$products7, _variantData$products8;

      setCustomAttributes(variantData === null || variantData === void 0 ? void 0 : (_variantData$products5 = variantData.products) === null || _variantData$products5 === void 0 ? void 0 : (_variantData$products6 = _variantData$products5.items[0]) === null || _variantData$products6 === void 0 ? void 0 : (_variantData$products7 = _variantData$products6.variants[0]) === null || _variantData$products7 === void 0 ? void 0 : (_variantData$products8 = _variantData$products7.product) === null || _variantData$products8 === void 0 ? void 0 : _variantData$products8.custom_attributes);
    }
  }, [variantData]);

  var _useMutation7 = useMutation(operations.addProductToQuote),
      _useMutation8 = _slicedToArray(_useMutation7, 2),
      addProductToQuote = _useMutation8[0],
      _useMutation8$ = _useMutation8[1],
      addtoQuotesData = _useMutation8$.data,
      loading = _useMutation8$.loading,
      error = _useMutation8$.error;

  var handleAddProductToQuote = useCallback( /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(data) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return addProductToQuote({
                fetchPolicy: 'no-cache',
                variables: {
                  sku: data.sku,
                  quantity: data.quantity ? data.quantity : 1,
                  selected_options: selectedOptionsArray
                }
              }).then(function (res) {
                var _res$data;

                dispatch(setQuotes(Number(res === null || res === void 0 ? void 0 : (_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.addConfigurableProductsToMpQuote.quote.items_count)));
                addToast({
                  message: 'Successfully added product to quote request!',
                  type: 'success',
                  timeout: '5000'
                });
              });

            case 2:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x2) {
      return _ref6.apply(this, arguments);
    };
  }(), [selectedOptionsArray]);
  var matchingVariant = useMemo(function () {
    return optionCodes && optionSelections && product.variants ? findMatchingVariant({
      optionCodes: optionCodes,
      optionSelections: optionSelections,
      variants: product.variants
    }) : null;
  }, [optionCodes, optionSelections, product.variants]);
  var configuredProductId = useMemo(function () {
    var _matchingVariant$prod;

    return isSignedIn ? matchingVariant === null || matchingVariant === void 0 ? void 0 : (_matchingVariant$prod = matchingVariant.product) === null || _matchingVariant$prod === void 0 ? void 0 : _matchingVariant$prod.id : null;
  }, [matchingVariant, isSignedIn]);

  var _useQuery4 = useQuery(EXISTS_IN_FAVORITES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      product_id_list: [configuredProductId]
    },
    skip: !configuredProductId
  }),
      favoritesData = _useQuery4.data,
      refetchFavorites = _useQuery4.refetch;

  var isInFavorites = useMemo(function () {
    var _favoritesData$existI;

    return isSignedIn ? favoritesData === null || favoritesData === void 0 ? void 0 : (_favoritesData$existI = favoritesData.existInFavorites[0]) === null || _favoritesData$existI === void 0 ? void 0 : _favoritesData$existI.exist : false;
  }, [favoritesData, isSignedIn]);
  return {
    breadcrumbCategoryId: breadcrumbCategoryId,
    errorMessage: derivedErrorMessage,
    handleAddToCart: handleAddToCart,
    handleSelectionChange: handleSelectionChange,
    isOutOfStock: isOutOfStock,
    isAddToCartDisabled: isOutOfStock || isMissingOptions || isAddConfigurableLoading || isAddSimpleLoading || isAddProductLoading,
    isSupportedProductType: isSupportedProductType,
    mediaGallery: mediaGallery,
    shouldShowWishlistButton: isSignedIn && storeConfigData && !!storeConfigData.storeConfig.magento_wishlist_general_is_enabled,
    productDetails: productDetails,
    wishlistButtonProps: wishlistButtonProps,
    wishlistItemOptions: wishlistItemOptions,
    defaultOptionSelection: derivedOptionSelections,
    disabledValuesMap: disabledValuesMap,
    configuredItemId: configuredItemId,
    configuredSmartString: configuredSmartString,
    customAttributes: customAttributes,
    weight: weight,
    successAddToCartToast: successAddToCartToast,
    handleResetProductConfiguration: handleResetProductConfiguration,
    isReset: isReset,
    optionSelections: optionSelections,
    isLoading: isAddConfigurableLoading || isAddSimpleLoading || isAddProductLoading,
    handleAddProductToQuote: handleAddProductToQuote,
    configCustomAttributes: configCustomAttributes,
    collectionsData: collectionsData === null || collectionsData === void 0 ? void 0 : (_collectionsData$coll = collectionsData.collections) === null || _collectionsData$coll === void 0 ? void 0 : _collectionsData$coll.collections,
    relatedProducts: dataRelated !== null && dataRelated !== void 0 && (_dataRelated$products = dataRelated.products) !== null && _dataRelated$products !== void 0 && _dataRelated$products.items ? dataRelated === null || dataRelated === void 0 ? void 0 : (_dataRelated$products2 = dataRelated.products) === null || _dataRelated$products2 === void 0 ? void 0 : _dataRelated$products2.items[0] : null,
    selectedOptionsArray: selectedOptionsArray,
    defaultSmartStringSelected: defaultSmartStringSelected,
    isInFavorites: isInFavorites,
    refetchFavorites: refetchFavorites,
    configuredVariant: matchingVariant === null || matchingVariant === void 0 ? void 0 : matchingVariant.product,
    setOptionSelections: setOptionSelections,
    setProductConfiguration: setProductConfiguration,
    tagValues: tagValues
  };
};